import { AdvancedGridTable } from "@apex/component-library";
import { Box } from "@chakra-ui/react";
import { ColDef } from "ag-grid-community";

import { RequirementsDetails } from "../../../api/useGetRequirements";
import { APP_ID } from "../../../utils/constants";
import { displayValue } from "../../../utils/formatVariables";
import getDateString from "../../../utils/getDateString";
import { getCurrencyValue } from "../../CallAlertsAuxWindow/auxUtils";

type Props = { requirementsDetails: RequirementsDetails[] };

export default function RequirementsDetailsTable({
  requirementsDetails,
}: Props) {
  if (requirementsDetails?.length === 0) {
    return (
      <Box p={4} bg="white">
        No Requirement Details
      </Box>
    );
  }

  const columnDefs: ColDef<RequirementsDetails>[] = [
    {
      headerName: "Trade Type",
      valueGetter: params => params.data?.type,
      width: 130,
      filter: "agTextColumnFilter",
      valueFormatter: ({ value }) => value.replace("REQ_", ""),
    },
    {
      headerName: "Account Type",
      valueGetter: params => params.data?.accountType,
      filter: "agTextColumnFilter",
      width: 160,
    },
    {
      headerName: "Order ID",
      valueGetter: params => displayValue(params.data?.orderID),
      filter: "agTextColumnFilter",
      width: 180,
    },
    {
      headerName: "Trade Number",
      valueGetter: params => displayValue(params.data?.tradeNumber),
      filter: "agTextColumnFilter",
      width: 160,
    },
    {
      headerName: "Trade Date",
      type: "date",
      valueGetter: params =>
        params.data?.tradeDate && getDateString(params.data?.tradeDate),
      width: 180,
    },
    {
      headerName: "Execution Time",
      valueGetter: params => params.data?.executionTime,
      valueFormatter: ({ value }) => value,
      filter: "agTextColumnFilter",
      width: 180,
    },
    {
      headerName: "Symbol",
      valueGetter: params => params.data?.symbol,
      filter: "agTextColumnFilter",
      width: 150,
    },
    {
      headerName: "Share Quantity",
      valueGetter: params => params.data?.shareQuantity,
      valueFormatter: ({ value }) => displayValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 160,
    },
    {
      headerName: "Net Amount",
      valueGetter: params => params.data?.netAmount,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 160,
    },
    {
      headerName: "Share Price",
      valueGetter: params => params.data?.sharePrice,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 160,
    },
    {
      headerName: "Curr. Code",
      headerTooltip: "Currency Code",
      valueGetter: params => params.data?.currencyCode,
      filter: "agTextColumnFilter",
      width: 160,
    },
    {
      headerName: "Requirement",
      valueGetter: params => params.data?.requirement,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 160,
    },
    {
      headerName: "Requirement Type",
      valueGetter: params => displayValue(params.data?.requirementType),
      filter: "agTextColumnFilter",
      width: 180,
    },
    {
      headerName: "Class",
      headerTooltip: "Underlying Symbol Class",
      valueGetter: params => params.data?.symbol,
      filter: "agTextColumnFilter",
      width: 120,
    },
    {
      headerName: "Unavailable Balance",
      valueGetter: params => params.data?.unavailableBalance,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 200,
    },
    {
      headerName: "Open or Close",
      valueGetter: params => params.data?.buySellCode,
      filter: "agTextColumnFilter",
      width: 180,
    },
    {
      headerName: "Pre Meet",
      valueGetter: params => params.data?.preMeet,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 150,
    },
    {
      headerName: "High Water Mark",
      valueGetter: params => params.data?.highWaterMark,
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 180,
    },
    {
      headerName: "Running Open Position",
      valueGetter: params => params.data?.runningOpenPosition,
      valueFormatter: ({ value }) => getCurrencyValue(value),
      filter: "agNumberColumnFilter",
      type: "rightAligned",
      width: 200,
    },
  ];

  return (
    <AdvancedGridTable
      columnDefs={columnDefs}
      rowData={requirementsDetails}
      gridId={`${APP_ID}.requirements-details-search-results`}
      data-testid="requirements-details-results"
      rowStyle={{ cursor: "pointer" }}
      rowClass="requirements-details-row"
      hasFiltersPanel
    />
  );
}
